/**
 * TanStack Query hooks for the PILA payment through PagoSimple (PF-485).
 *
 * Query-key contract (scoped under the active company):
 *   ['companies', teamId, companyId, 'payroll-pila']                           — root
 *   ['companies', teamId, companyId, 'payroll-pila', 'availability']
 *   ['companies', teamId, companyId, 'payroll-pila', 'catalog']
 *   ['companies', teamId, companyId, 'payroll-pila', 'settings']
 *   ['companies', teamId, companyId, 'payroll-pila', 'affiliations', employeeId]
 *   ['companies', teamId, companyId, 'payroll-pila', 'preflight', period]
 *   ['companies', teamId, companyId, 'payroll-pila', 'list']
 *   ['companies', teamId, companyId, 'payroll-pila', 'detail', planillaId]
 *
 * A confirmed payment posts a journal entry and a bank movement, so the
 * refresh invalidates the journal panel through the company root.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  generatePilaPlanilla,
  getPilaAffiliations,
  getPilaAvailability,
  getPilaCatalog,
  getPilaPlanilla,
  getPilaPreflight,
  getPilaSettings,
  linkPagoSimple,
  listPilaPlanillas,
  recordPilaFspAdjustment,
  refreshPilaStatus,
  replacePilaPlanilla,
  requestPilaPaymentUrl,
  updatePilaAffiliations,
  updatePilaSettings,
  type PilaCatalog,
  type PilaEmployeeAffiliations,
  type PilaFspAdjustment,
  type PilaPlanillaDetail,
  type PilaPlanillaSummary,
  type PilaPreflight,
  type PilaRefreshOutcome,
  type PilaSettings,
  type UpdatePilaAffiliationsInput,
  type UpdatePilaSettingsInput,
} from "@/lib/api/payroll-pila";
import { companiesKeys } from "@/lib/api/companies.queries";
import { useActiveTeam } from "@/lib/team-context";

export const pilaKeys = {
  root: (teamId: string | null, companyId: string) =>
    [...companiesKeys.detail(teamId, companyId), "payroll-pila"] as const,
  availability: (teamId: string | null, companyId: string) =>
    [...pilaKeys.root(teamId, companyId), "availability"] as const,
  catalog: (teamId: string | null, companyId: string) =>
    [...pilaKeys.root(teamId, companyId), "catalog"] as const,
  settings: (teamId: string | null, companyId: string) =>
    [...pilaKeys.root(teamId, companyId), "settings"] as const,
  affiliations: (
    teamId: string | null,
    companyId: string,
    employeeId: string,
  ) =>
    [...pilaKeys.root(teamId, companyId), "affiliations", employeeId] as const,
  preflight: (teamId: string | null, companyId: string, period: string) =>
    [...pilaKeys.root(teamId, companyId), "preflight", period] as const,
  list: (teamId: string | null, companyId: string) =>
    [...pilaKeys.root(teamId, companyId), "list"] as const,
  detail: (teamId: string | null, companyId: string, planillaId: string) =>
    [...pilaKeys.root(teamId, companyId), "detail", planillaId] as const,
};

const NO_COMPANY = ["payroll-pila", "__no_company__"] as const;

/** Whether this deployment offers PILA payment (production stays off until PF-493). */
export function usePilaAvailability(companyId: string | undefined) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<{ enabled: boolean }>({
    queryKey: companyId
      ? pilaKeys.availability(activeTeamId, companyId)
      : [...NO_COMPANY, "availability"],
    queryFn: () => getPilaAvailability(companyId!),
    enabled: !!companyId && !!activeTeamId,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function usePilaCatalog(companyId: string | undefined, enabled = true) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<PilaCatalog>({
    queryKey: companyId
      ? pilaKeys.catalog(activeTeamId, companyId)
      : [...NO_COMPANY, "catalog"],
    queryFn: () => getPilaCatalog(companyId!),
    enabled: enabled && !!companyId && !!activeTeamId,
    staleTime: 60 * 60_000,
  });
}

export function usePilaSettings(companyId: string | undefined, enabled = true) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<PilaSettings>({
    queryKey: companyId
      ? pilaKeys.settings(activeTeamId, companyId)
      : [...NO_COMPANY, "settings"],
    queryFn: () => getPilaSettings(companyId!),
    enabled: enabled && !!companyId && !!activeTeamId,
  });
}

export function usePilaAffiliations(
  companyId: string | undefined,
  employeeId: string | undefined,
  enabled = true,
) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<PilaEmployeeAffiliations>({
    queryKey:
      companyId && employeeId
        ? pilaKeys.affiliations(activeTeamId, companyId, employeeId)
        : [...NO_COMPANY, "affiliations", employeeId ?? "x"],
    queryFn: () => getPilaAffiliations(companyId!, employeeId!),
    enabled: enabled && !!companyId && !!employeeId && !!activeTeamId,
  });
}

export function usePilaPreflight(
  companyId: string | undefined,
  period: string,
  enabled = true,
) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<PilaPreflight>({
    queryKey: companyId
      ? pilaKeys.preflight(activeTeamId, companyId, period)
      : [...NO_COMPANY, "preflight", period],
    queryFn: () => getPilaPreflight(companyId!, period),
    enabled:
      enabled && !!companyId && !!activeTeamId && /^\d{4}-\d{2}$/.test(period),
  });
}

export function usePilaPlanillas(
  companyId: string | undefined,
  enabled = true,
) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<{ planillas: PilaPlanillaSummary[] }>({
    queryKey: companyId
      ? pilaKeys.list(activeTeamId, companyId)
      : [...NO_COMPANY, "list"],
    queryFn: () => listPilaPlanillas(companyId!),
    enabled: enabled && !!companyId && !!activeTeamId,
  });
}

export function usePilaPlanilla(
  companyId: string | undefined,
  planillaId: string | undefined,
) {
  const { activeTeamId } = useActiveTeam();
  return useQuery<PilaPlanillaDetail>({
    queryKey:
      companyId && planillaId
        ? pilaKeys.detail(activeTeamId, companyId, planillaId)
        : [...NO_COMPANY, "detail", planillaId ?? "x"],
    queryFn: () => getPilaPlanilla(companyId!, planillaId!),
    enabled: !!companyId && !!planillaId && !!activeTeamId,
  });
}

export function useUpdatePilaSettings(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<PilaSettings, Error, UpdatePilaSettingsInput>({
    mutationFn: (input) => {
      if (!companyId) throw new Error("companyId required");
      return updatePilaSettings(companyId, input);
    },
    onSuccess: (data) => {
      if (!companyId) return;
      qc.setQueryData(pilaKeys.settings(activeTeamId, companyId), data);
      void qc.invalidateQueries({
        queryKey: pilaKeys.root(activeTeamId, companyId),
      });
    },
  });
}

export function useLinkPagoSimple(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<PilaSettings, Error, void>({
    mutationFn: () => {
      if (!companyId) throw new Error("companyId required");
      return linkPagoSimple(companyId);
    },
    onSuccess: (data) => {
      if (!companyId) return;
      qc.setQueryData(pilaKeys.settings(activeTeamId, companyId), data);
      void qc.invalidateQueries({
        queryKey: pilaKeys.root(activeTeamId, companyId),
      });
    },
  });
}

export function useRecordPilaFspAdjustment(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<
    { adjustment: PilaFspAdjustment; created: boolean },
    Error,
    { period: string; employeeId: string }
  >({
    mutationFn: (input) => {
      if (!companyId) throw new Error("companyId required");
      return recordPilaFspAdjustment(companyId, input);
    },
    onSuccess: () => {
      if (!companyId) return;
      // A journal entry was posted: refresh the month and the accounting panels.
      void qc.invalidateQueries({
        queryKey: companiesKeys.detail(activeTeamId, companyId),
      });
    },
  });
}

export function useUpdatePilaAffiliations(
  companyId: string | undefined,
  employeeId: string | undefined,
) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return useMutation<
    PilaEmployeeAffiliations,
    Error,
    UpdatePilaAffiliationsInput
  >({
    mutationFn: (input) => {
      if (!companyId || !employeeId)
        throw new Error("companyId and employeeId required");
      return updatePilaAffiliations(companyId, employeeId, input);
    },
    onSuccess: (data) => {
      if (!companyId || !employeeId) return;
      qc.setQueryData(
        pilaKeys.affiliations(activeTeamId, companyId, employeeId),
        data,
      );
      // Preflight blockers depend on the affiliations.
      void qc.invalidateQueries({
        queryKey: [...pilaKeys.root(activeTeamId, companyId), "preflight"],
      });
    },
  });
}

function useStorePlanilla(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  return (planilla: PilaPlanillaDetail, invalidateCompany = false) => {
    if (!companyId) return;
    qc.setQueryData(
      pilaKeys.detail(activeTeamId, companyId, planilla.id),
      planilla,
    );
    void qc.invalidateQueries({
      queryKey: pilaKeys.list(activeTeamId, companyId),
    });
    void qc.invalidateQueries({
      queryKey: [...pilaKeys.root(activeTeamId, companyId), "preflight"],
    });
    // The payment posted a journal entry and a bank movement.
    if (invalidateCompany)
      void qc.invalidateQueries({
        queryKey: companiesKeys.detail(activeTeamId, companyId),
      });
  };
}

export function useGeneratePilaPlanilla(companyId: string | undefined) {
  const store = useStorePlanilla(companyId);
  return useMutation<PilaPlanillaDetail, Error, { period: string }>({
    mutationFn: ({ period }) => {
      if (!companyId) throw new Error("companyId required");
      return generatePilaPlanilla(companyId, period);
    },
    onSuccess: (data) => store(data),
  });
}

export function useReplacePilaPlanilla(companyId: string | undefined) {
  const qc = useQueryClient();
  const { activeTeamId } = useActiveTeam();
  const store = useStorePlanilla(companyId);
  return useMutation<PilaPlanillaDetail, Error, { planillaId: string }>({
    mutationFn: ({ planillaId }) => {
      if (!companyId) throw new Error("companyId required");
      return replacePilaPlanilla(companyId, planillaId);
    },
    onSuccess: (data, vars) => {
      store(data);
      if (companyId)
        void qc.invalidateQueries({
          queryKey: pilaKeys.detail(activeTeamId, companyId, vars.planillaId),
        });
    },
  });
}

export function useRequestPilaPaymentUrl(companyId: string | undefined) {
  const store = useStorePlanilla(companyId);
  return useMutation<
    { url: string; planilla: PilaPlanillaDetail },
    Error,
    { planillaId: string; bankAccountId?: string | null }
  >({
    mutationFn: ({ planillaId, bankAccountId }) => {
      if (!companyId) throw new Error("companyId required");
      return requestPilaPaymentUrl(companyId, planillaId, bankAccountId);
    },
    onSuccess: (data) => store(data.planilla),
  });
}

export function useRefreshPilaStatus(companyId: string | undefined) {
  const store = useStorePlanilla(companyId);
  return useMutation<
    { outcome: PilaRefreshOutcome; planilla: PilaPlanillaDetail },
    Error,
    { planillaId: string }
  >({
    mutationFn: ({ planillaId }) => {
      if (!companyId) throw new Error("companyId required");
      return refreshPilaStatus(companyId, planillaId);
    },
    onSuccess: (data) => store(data.planilla, data.outcome === "paid"),
  });
}
